import React, { memo, useCallback, useMemo } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { isToday, parseISO } from 'date-fns';
import { useAppSelector } from 'store/hooks';
import { Button, CardView, hitSlopS, rem, Separator, Spacer } from 'design-system';
import BlurAmountView from 'design-system/BlurAmountView';
import { IconRocket } from 'design-system/icons/IconRocket';
import IconTooltip from 'design-system/icons/IconTooltip';
import Logo from 'design-system/Logo';
import Squircle from 'design-system/Squircle';
import Text from 'design-system/Text';
import { selectIsScrambled, useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { fundingAccountToolTipStrings } from 'features/invest/constants';
import AutosavePauseModal from 'features/pots/components/AutosavePauseModal';
import { selectLatestAutosaveActivity, selectLatestUpcomingPayment } from 'features/pots/reducers/selectors';
import { selectAutosaveStatusObj } from 'reducers/selectors';
import { formatAmount, formatSortCodeAndAccountNumber } from 'utils';
import createStyleSheets from 'utils/createStyleSheets';
import { isWithinNext24Hours } from 'utils/dates';
import Modal from 'utils/packages/Modal';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { showUnavailableOnWeb } from 'utils/webGate';
import { editSavingsPlan } from '../actions';
import { useNavigateToFixAutosave } from '../hooks/useNavigateToFixAutosave';
import { formatPauseAutosaveDate, getSavingsPlanErrorButtonCTA, getSavingsPlanErrorInfo } from '../utils';
import AutosaveStatus from './AutosaveStatus';
const fetchOptions = {
    throws: true,
    muteError: true,
    muteResult: true,
};
const DirectDebitSection = ({ autoSaveId }) => {
    const navigation = useMainStackNavigation();
    const [, , fetching, fetch] = useFetchOld(fetchOptions);
    const colors = useColors();
    const styles = useStyles(styleSet);
    const scrambled = useAppSelector(selectIsScrambled);
    const autosaveStatusObj = useAppSelector(selectAutosaveStatusObj);
    const isPaused = useAppSelector((state) => state.pots.savingsPlan?.isPaused);
    const bankInfo = useAppSelector((state) => state.pots.savingsPlan?.bankInfo);
    const sortCodeStr = useAppSelector((state) => state.pots.savingsPlan?.sortCode);
    const pausedUntil = useAppSelector((state) => state.pots.savingsPlan?.pausedUntil);
    const accountNumberStr = useAppSelector((state) => state.pots.savingsPlan?.accountNumber);
    const savingsPlanMandateStatus = useAppSelector((state) => state.pots.savingsPlan?.mandateStatus);
    const mandateStatusDescription = useAppSelector((state) => state.pots.savingsPlan?.mandateStatusDescription);
    const upcomingPayment = useAppSelector(selectLatestUpcomingPayment);
    const latestActivity = useAppSelector(selectLatestAutosaveActivity);
    const navigateToFixAutosave = useNavigateToFixAutosave(autosaveStatusObj?.actionRequired);
    const onChangePauseAutosave = useCallback(async () => {
        if (showUnavailableOnWeb('emma://auto-save', 'Manage Autosave')) {
            return;
        }
        const activityPending = latestActivity?.status === 'PENDING';
        const onPause = () => navigation.navigateDeprecated('PauseAutosaveInfoScreen', { autoSaveId, processingActivity: activityPending });
        if (!isPaused) {
            if (upcomingPayment && isWithinNext24Hours(upcomingPayment.executionDate)) {
                AutosavePauseModal.show({
                    title: 'Pause autosave?',
                    subtitle: `Pausing will stop future autosaves.\nYou still have a ${formatAmount(upcomingPayment.amount.value, upcomingPayment.amount.currency)} autosave scheduled for ${isToday(parseISO(upcomingPayment.executionDate)) ? 'today' : 'tomorrow'}.\nWould you like to skip ${isToday(parseISO(upcomingPayment.executionDate)) ? 'today' : 'tomorrow'}'s payment as well?`,
                    onPause,
                    onPauseAndSkip: () => navigation.navigateDeprecated('PauseAutosaveInfoScreen', { autoSaveId, paymentToSkip: upcomingPayment }),
                });
            }
            else if (latestActivity && latestActivity.status === 'PENDING') {
                Modal.showConfirmation({
                    title: 'Pause autosave?',
                    subtitle: `Your ${formatAmount(latestActivity.amount.value, latestActivity.amount.currency)} autosave is already being processed and can't be skipped.\nPausing now will stop future autosaves only.`,
                    confirmButtonTitle: 'Pause',
                    cancelButtonTitle: 'Cancel',
                    onConfirm: onPause,
                });
            }
            else {
                onPause();
            }
        }
        else {
            try {
                await fetch(editSavingsPlan(autoSaveId, { isPaused: false }));
            }
            catch {
                //
            }
        }
    }, [autoSaveId, isPaused, latestActivity, upcomingPayment]);
    const renderDetails = useMemo(() => {
        if (!sortCodeStr && !accountNumberStr)
            return null;
        return (<BlurAmountView enabled={scrambled}>
        <Text TextThin-14 numberOfLines={1} ellipsizeMode="tail" Secondary>
          {formatSortCodeAndAccountNumber(sortCodeStr, accountNumberStr)}
        </Text>
      </BlurAmountView>);
    }, [accountNumberStr, scrambled, sortCodeStr]);
    const renderActionRequired = useMemo(() => {
        if (autosaveStatusObj?.actionRequired) {
            const info = getSavingsPlanErrorInfo(autosaveStatusObj?.actionRequired, bankInfo?.bankName);
            return (<View style={styles.textWrapper}>
          <Text TextThin-14 Secondary>
            {info}
          </Text>
        </View>);
        }
        return undefined;
    }, [autosaveStatusObj?.actionRequired, bankInfo?.bankName, styles.textWrapper]);
    const onResumeAutosave = useCallback(() => {
        if (showUnavailableOnWeb('emma://auto-save', 'Manage Autosave')) {
            return;
        }
        navigateToFixAutosave();
    }, [navigateToFixAutosave]);
    const showFundingAccountTooltip = useCallback(() => {
        Modal.showConfirmation({
            title: fundingAccountToolTipStrings.fundingAccountTitle,
            subtitle: fundingAccountToolTipStrings.fundingAccountDesc,
            confirmButtonTitle: 'Okay',
        });
    }, []);
    if (!autosaveStatusObj || !bankInfo) {
        return null;
    }
    return (<CardView noMarginBottom noMarginTop>
      <View style={styles.rowAccount}>
        <Squircle color={colors.background.dark}>
          <IconRocket colorKey="brand"/>
        </Squircle>
        <Spacer.W16 />
        <View style={styles.flex}>
          <AutosaveStatus />
          <Text Text-16>Autosave</Text>
        </View>
      </View>

      <Separator />

      <Spacer.H16 />

      {!!mandateStatusDescription && !!autosaveStatusObj?.isPending && !autosaveStatusObj?.actionRequired && (<View style={styles.textWrapper}>
          <Text TextThin-14 Secondary>
            {mandateStatusDescription}
          </Text>
        </View>)}

      {!mandateStatusDescription &&
            !autosaveStatusObj?.actionRequired &&
            savingsPlanMandateStatus === 'ACTIVE' &&
            !!pausedUntil && (<View style={styles.textWrapper}>
            <Text TextThin-14 Secondary>
              Your autosave is on hold until {formatPauseAutosaveDate(new Date(pausedUntil))}. Tap resume to start
              again.
            </Text>
          </View>)}

      {autosaveStatusObj.actionRequired ? (renderActionRequired) : (<>
          <View style={styles.row}>
            <Text TextThin-14 Secondary>
              Funding account
            </Text>
            <Spacer w={6}/>
            <TouchableOpacity hitSlop={hitSlopS} onPress={showFundingAccountTooltip}>
              <IconTooltip height={16} width={16} colorKey="secondary"/>
            </TouchableOpacity>
          </View>
          <View style={styles.bankInfoCard}>
            <Logo R24 uri={bankInfo.bankIconUrl}/>
            <Spacer w={16}/>
            <View style={styles.flex}>{renderDetails}</View>
          </View>
        </>)}

      <View style={styles.horizontalPadding}>
        {autosaveStatusObj.actionRequired ? (<Button square errorReversed onPress={onResumeAutosave} title={getSavingsPlanErrorButtonCTA(autosaveStatusObj.actionRequired)}/>) : (<Button square brandReversed loading={fetching} onPress={onChangePauseAutosave} title={isPaused ? 'Resume autosave' : 'Pause autosave'}/>)}
      </View>
      <Spacer h={16}/>
    </CardView>);
};
const styleSet = createStyleSheets((colors) => ({
    horizontalPadding: {
        paddingHorizontal: rem(16),
    },
    flex: {
        flex: 1,
    },
    rowAccount: {
        padding: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
    },
    textContainer: {
        flex: 1,
        flexGrow: 1,
        justifyContent: 'center',
        paddingHorizontal: rem(16),
    },
    bankInfoCard: {
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
        borderRadius: rem(12),
        marginBottom: rem(16),
        marginHorizontal: rem(16),
        backgroundColor: colors.background.dark,
    },
    textWrapper: {
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
    },
}));
export default memo(DirectDebitSection);
